import { useCallback, useEffect, useMemo, useState, useRef } from 'react';

import Step from './Step';
import FormStatus from './FormStatus';
import './verticalStepper.scss';
import { saveRequest } from '@forms/input-forms/helpers';
import { useRequest } from '@stores';
import classNames from 'classnames';
import { useNonPcnUpdateMutation } from '@src/api/services/non-pcn/hooks/useNonPcnUpdateMutation';
import { useFileStorageUploadMutation } from '@src/api/services/file-storage/hooks/useFileStorageUploadMutation';
import { useNavigate, useLocation } from '@tanstack/react-router';

const toStepSlug = (value = '') =>
  value
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

const VerticalStepper = () => {
  const [currentSection, setCurrentSection] = useState(0);

  const {
    getActiveSection,
    getSections,
    isFormReadOnly: isReadOnly,
    isSectionHidden,
    isSectionTouched,
    selectedRequestId,
    setActiveSection,
    updateSection,
  } = useRequest();

  const activeSection = getActiveSection();
  const sections = getSections();
  const reqTypeId = Number(selectedRequestId);
  const visibleSections = sections.filter((section) => !section.hidden);
  const currentStepNumber = visibleSections.findIndex((section) => section.id === activeSection?.id) + 1;
  const pendingHashRef = useRef(null);

  const { mutateAsync: updateNonPcn } = useNonPcnUpdateMutation();
  const { mutateAsync: uploadRequestFiles } = useFileStorageUploadMutation();

  // @TODO: Add the rest of the request types as update logic gets migrated to React-Query/Zustand
  const updateFnMapping = useMemo(
    () => ({
      11: updateNonPcn,
    }),
    [updateNonPcn]
  );

  const navigate = useNavigate();

  const { pathname, hash: locationHash } = useLocation({
    select: (location) => ({
      pathname: location.pathname,
      hash: location.hash ?? '',
    }),
  });
  const duplicateSlugCounts = useMemo(() => {
    const counts = {};
    sections.forEach((section) => {
      if (section.hidden) return;
      const slug = toStepSlug(section.label || '');
      if (!slug) return;
      counts[slug] = (counts[slug] || 0) + 1;
    });
    return counts;
  }, [sections]);

  const parseStepHash = useCallback(
    (hash = '') => {
      const clean = decodeURIComponent(hash).replace(/^#/, '').trim();
      if (!clean) return { id: null, slug: '' };

      // First try full slug match (handles unique slugs, including names ending in numbers)
      if ((duplicateSlugCounts[clean] || 0) > 0) {
        return { id: null, slug: clean };
      }

      // Fallback: duplicated format "slug-id"
      const match = clean.match(/^(.*)-(\d+)$/);
      if (!match) return { id: null, slug: clean };

      const baseSlug = match[1];
      const id = Number(match[2]);
      const isDuplicate = (duplicateSlugCounts[baseSlug] || 0) > 1;

      return isDuplicate ? { id, slug: baseSlug } : { id: null, slug: clean };
    },
    [duplicateSlugCounts]
  );

  const updateStepHash = useCallback(
    (stepName = '', stepId = null) => {
      const slug = toStepSlug(stepName);
      if (!slug) return;

      const isDuplicate = (duplicateSlugCounts[slug] || 0) > 1;
      const hashValue = isDuplicate && stepId != null ? `${slug}-${stepId}` : slug;

      const currentHash = decodeURIComponent(locationHash).replace(/^#/, '').trim();
      if (currentHash === hashValue) {
        pendingHashRef.current = null;
        return;
      }

      pendingHashRef.current = hashValue;

      void navigate({
        to: pathname,
        search: (prev) => prev,
        hash: hashValue,
        replace: true,
      });
    },
    [pathname, navigate, duplicateSlugCounts, locationHash]
  );

  const handleClick = useCallback(
    (e) => {
      const sectionId = Number(e.currentTarget.dataset.stepid);
      const section = sections.find((section) => section.id === sectionId);

      const targetId = section.isParentStep ? sectionId + 1 : sectionId;
      const targetSection = sections.find((section) => section.id === targetId);

      if (!targetSection) return;

      const stepName = targetSection?.label || section?.label || '';

      if (section.isParentStep) {
        updateSection(section.id, { touched: true });
      }
      if (section.isSubStep) {
        updateSection(section.parentId, { touched: true });
      }

      if (activeSection?.id !== targetId) {
        setActiveSection(targetSection);
        window.scrollTo(0, 0);
        !isReadOnly && saveRequest(false, uploadRequestFiles, updateFnMapping[reqTypeId]);
      }

      updateStepHash(stepName, targetSection.id);

      section.isParentStep && document.querySelector('[data-stepid="' + targetId + '"]')?.focus();
    },
    [
      activeSection?.id,
      sections,
      updateSection,
      isReadOnly,
      uploadRequestFiles,
      updateFnMapping,
      reqTypeId,
      setActiveSection,
      updateStepHash,
    ]
  );

  useEffect(() => {
    if (!(activeSection?.id >= 0)) return;

    const activeSectionId = activeSection.id;
    const prevSectionId = currentSection;
    const isTouched = isSectionTouched(prevSectionId);

    if (prevSectionId !== activeSectionId) {
      const prevSection = sections.find((section) => section.id === prevSectionId);
      const isParentTouched = isSectionTouched(prevSection?.parentId);

      !isTouched && updateSection(prevSectionId, { touched: true });
      prevSection?.parentId && !isParentTouched && updateSection(prevSection.parentId, { touched: true });
    }

    setCurrentSection(activeSectionId);
  }, [activeSection?.id, sections, isSectionTouched, updateSection, currentSection]);

  useEffect(() => {
    if (!activeSection?.label) return;
    updateStepHash(activeSection.label, activeSection.id);
  }, [activeSection?.label, activeSection?.id, updateStepHash]);

  const containerStyles = classNames('vertical-stepper', {
    'read-only': isReadOnly,
  });

  useEffect(() => {
    if (!sections.length) return;

    const currentHash = decodeURIComponent(locationHash).replace(/^#/, '').trim();

    // Prevent activeSection<->hash ping-pong while router hash is still catching up.
    if (pendingHashRef.current) {
      if (currentHash !== pendingHashRef.current) return;
      pendingHashRef.current = null;
    }

    const { id, slug } = parseStepHash(locationHash);
    if (id == null && !slug) return;

    const matched =
      id != null
        ? sections.find((section) => !section.hidden && section.id === id)
        : sections.find((section) => !section.hidden && toStepSlug(section.label || '') === slug);
    if (!matched) return;

    const targetSection = matched.isParentStep ? sections.find((section) => section.id === matched.id + 1) : matched;
    if (!targetSection) return;

    if (activeSection?.id !== targetSection.id) {
      if (matched.isParentStep) updateSection(matched.id, { touched: true });
      if (targetSection.isSubStep) updateSection(targetSection.parentId, { touched: true });

      setActiveSection(targetSection);
      window.scrollTo(0, 0);
    }
  }, [sections, setActiveSection, updateSection, activeSection?.id, parseStepHash, locationHash]);

  return (
    <div className={containerStyles}>
      <FormStatus />
      <div className='step-status'>
        Current Step: {currentStepNumber} of {visibleSections.length}
      </div>
      {sections.map(
        (section) => !isSectionHidden(section.id) && <Step key={section.id} onClick={handleClick} step={section} />
      )}
    </div>
  );
};

export default VerticalStepper;
